import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { openModalAction } from '../../../actions/modal';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import Select from '../../../components/Select';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { DateFilters } from '../../../utils/DateFilters';
import actions from './redux/actions';
import {
  buildCheckboxFilterDefinition,
  getAdvancedFilterValues,
  isCustomDateRangeOption,
  resolveDateRange,
} from './shared';

import './MessagesOverview.scss';

const DEFAULT_LIMIT = 200;
const FAILED_EVENTS_DATE_FILTER_OPTIONS = [
  DateFilters.Custom,
  DateFilters.Last48Hours,
  DateFilters.Last7Days,
  DateFilters.Last30Days,
  DateFilters.Last6Months,
  DateFilters.Last12Months,
];
const DEFAULT_DATE_FILTER = DateFilters.Last12Months;
const FAILED_EVENTS_FILTER_GROUPS = {
  category: 'healthFailedEventsCategory',
  fixed: 'healthFailedEventsFixed',
  reviewed: 'healthFailedEventsReviewed',
};

const BOOLEAN_FILTER_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

function TruncatedCell({ value }) {
  const text = value ? String(value) : '-';
  return (
    <div className="health-event-details-table__truncated" title={text}>
      {text}
    </div>
  );
}

TruncatedCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function renderDateTimeCell(info) {
  const value = info.getValue();
  return value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-';
}

function renderValueOrDashCell(info) {
  return info.getValue() || '-';
}

function renderTruncatedCell(info) {
  return <TruncatedCell value={info.getValue()} />;
}

function toCsv(values) {
  return values?.length ? values.join(',') : undefined;
}

function buildFilterDefinitions(bundle) {
  return {
    ...buildCheckboxFilterDefinition(
      FAILED_EVENTS_FILTER_GROUPS.category,
      'Fail category',
      (bundle.fail_categories || []).map(category => ({ value: category, label: category })),
    ),
    ...buildCheckboxFilterDefinition(FAILED_EVENTS_FILTER_GROUPS.fixed, 'Fixed', BOOLEAN_FILTER_OPTIONS),
    ...buildCheckboxFilterDefinition(FAILED_EVENTS_FILTER_GROUPS.reviewed, 'Reviewed', BOOLEAN_FILTER_OPTIONS),
  };
}

function FailedEventsTab({
  filters,
  getFailedEvents,
  getFailedEventsFormBundle,
  openDateRangeSelectionModal,
  updateFailedEvent,
}) {
  const [bundle, setBundle] = React.useState({ fail_categories: [] });
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: DEFAULT_LIMIT, totalRecords: 0 });
  const [dateFilterValue, setDateFilterValue] = React.useState(DEFAULT_DATE_FILTER.value);
  const [dateRange, setDateRange] = React.useState(resolveDateRange(DEFAULT_DATE_FILTER));
  const [capIdQuery, setCapIdQuery] = React.useState('');
  const pageRequest = React.useRef({ offset: 0, limit: DEFAULT_LIMIT });

  React.useEffect(() => {
    getFailedEventsFormBundle().then(resp => {
      setBundle(resp?.response || { fail_categories: [] });
    });
  }, [getFailedEventsFormBundle]);

  const selectedCategories = React.useMemo(
    () => getAdvancedFilterValues(filters, FAILED_EVENTS_FILTER_GROUPS.category),
    [filters],
  );
  const selectedFixed = React.useMemo(
    () => getAdvancedFilterValues(filters, FAILED_EVENTS_FILTER_GROUPS.fixed),
    [filters],
  );
  const selectedReviewed = React.useMemo(
    () => getAdvancedFilterValues(filters, FAILED_EVENTS_FILTER_GROUPS.reviewed),
    [filters],
  );

  const loadData = React.useCallback(() => {
    setIsLoading(true);
    setError('');

    const params = {
      offset: pageRequest.current.offset,
      limit: pageRequest.current.limit,
      fail_categories: toCsv(selectedCategories),
      cap_id: capIdQuery.trim() || undefined,
    };

    if (selectedFixed.length === 1) {
      params.fixed = selectedFixed[0];
    }
    if (selectedReviewed.length === 1) {
      params.reviewed = selectedReviewed[0];
    }
    const requestRange = dateFilterValue === 'custom'
      ? dateRange
      : resolveDateRange({ value: dateFilterValue });
    if (requestRange.start) {
      params.start_at = requestRange.start.clone().utc().toISOString();
    }
    if (requestRange.end) {
      params.end_at = requestRange.end.clone().utc().toISOString();
    }

    getFailedEvents(params)
      .then(resp => {
        const response = resp?.response || {};
        setRows(response.data || []);
        setPagination({
          offset: response.offset || 0,
          limit: response.limit || pageRequest.current.limit,
          totalRecords: response.totalRecords || 0,
        });
        setIsLoading(false);
      })
      .catch(() => {
        setError('Unable to load failed events.');
        setIsLoading(false);
      });
  }, [capIdQuery, dateFilterValue, dateRange.end, dateRange.start, getFailedEvents, selectedCategories, selectedFixed, selectedReviewed]);

  React.useEffect(() => {
    pageRequest.current.offset = 0;
    loadData();
  }, [loadData]);

  const filterDefinitions = React.useMemo(() => buildFilterDefinitions(bundle), [bundle]);

  const onDateRangeChanged = React.useCallback(option => {
    if (isCustomDateRangeOption(option)) {
      const onDateSubmited = dates => {
        setDateFilterValue('custom');
        setDateRange({
          start: dates.startDate ? moment(dates.startDate * 1000) : undefined,
          end: dates.endDate ? moment(dates.endDate * 1000) : undefined,
        });
      };
      openDateRangeSelectionModal({ actionOnDone: onDateSubmited });
      return;
    }

    setDateFilterValue(option?.value || DEFAULT_DATE_FILTER.value);
    setDateRange(resolveDateRange(option || DEFAULT_DATE_FILTER));
  }, [openDateRangeSelectionModal]);

  const onToggleField = React.useCallback((id, key, value) => {
    updateFailedEvent(id, { [key]: value })
      .then(() => {
        setRows(prevRows => prevRows.map(row => (row.id === id ? { ...row, [key]: value } : row)));
      })
      .catch(() => {
        setError('Unable to update failed event.');
      });
  }, [updateFailedEvent]);

  const renderFixedCell = React.useCallback(
    info => (
      <input
        type="checkbox"
        checked={Boolean(info.row.original.fixed)}
        onChange={event => onToggleField(info.row.original.id, 'fixed', event.target.checked)}
      />
    ),
    [onToggleField],
  );

  const renderReviewedCell = React.useCallback(
    info => (
      <input
        type="checkbox"
        checked={Boolean(info.row.original.reviewed)}
        onChange={event => onToggleField(info.row.original.id, 'reviewed', event.target.checked)}
      />
    ),
    [onToggleField],
  );

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('created_at', {
        header: 'Created at',
        cell: renderDateTimeCell,
        size: 180,
      }),
      columnHelper.accessor('cap_id', {
        header: 'Cap ID',
        cell: renderValueOrDashCell,
        size: 180,
      }),
      columnHelper.accessor('org_name', {
        header: 'Organization',
        cell: renderValueOrDashCell,
        size: 180,
      }),
      columnHelper.accessor('request', {
        header: 'Request',
        cell: renderTruncatedCell,
        size: 420,
      }),
      columnHelper.accessor('fail_category', {
        header: 'Fail category',
        cell: renderValueOrDashCell,
        size: 220,
      }),
      columnHelper.accessor('failure_source', {
        header: 'Failure source',
        cell: renderValueOrDashCell,
        size: 140,
      }),
      columnHelper.accessor('fail_reason', {
        header: 'Fail reason',
        cell: renderTruncatedCell,
        size: 320,
      }),
      columnHelper.accessor('fixed', {
        header: 'Fixed',
        cell: renderFixedCell,
      }),
      columnHelper.accessor('reviewed', {
        header: 'Reviewed',
        cell: renderReviewedCell,
      }),
    ],
    [renderFixedCell, renderReviewedCell],
  );

  return (
    <>
      <PageHeader
        left="Failed events"
        right={(
          <div className="health-messages__table-header-actions">
            <Select
              id="health-failed-events-date-range"
              data={FAILED_EVENTS_DATE_FILTER_OPTIONS}
              value={dateFilterValue}
              onChange={onDateRangeChanged}
            />
            <TextInput
              class="search"
              placeholder="Cap ID"
              value={capIdQuery}
              onChange={event => setCapIdQuery(event.target.value)}
            />
            <AdvancedFilters>
              <Filter definition={filterDefinitions[FAILED_EVENTS_FILTER_GROUPS.category]} />
              <Filter definition={filterDefinitions[FAILED_EVENTS_FILTER_GROUPS.fixed]} />
              <Filter definition={filterDefinitions[FAILED_EVENTS_FILTER_GROUPS.reviewed]} />
            </AdvancedFilters>
            <Button class="health-refresh-button" onClick={loadData} title="Refresh" />
          </div>
        )}
      />
      <AdvancedFiltersBar customFilters={filterDefinitions} />

      {error ? <div className="health-messages-error">{error}</div> : null}

      <Table3
        name="health-failed-events"
        data={rows}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        onOffsetChange={offset => {
          pageRequest.current.offset = offset;
          loadData();
        }}
        onPageSizeChange={limit => {
          pageRequest.current.limit = limit;
          pageRequest.current.offset = 0;
          loadData();
        }}
      />

    </>
  );
}

FailedEventsTab.propTypes = {
  filters: PropTypes.array.isRequired,
  getFailedEvents: PropTypes.func.isRequired,
  getFailedEventsFormBundle: PropTypes.func.isRequired,
  openDateRangeSelectionModal: PropTypes.func.isRequired,
  updateFailedEvent: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getFailedEvents: params => dispatch(actions.getFailedEvents(params)),
  getFailedEventsFormBundle: () => dispatch(actions.getFailedEventsFormBundle()),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  updateFailedEvent: (id, data) => dispatch(actions.updateFailedEvent(id, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FailedEventsTab);
